import React, { useEffect, useRef, useState } from "react";
import type { LottieRefCurrentProps } from "lottie-react";
import { Link } from "react-router-dom";
import {
  Check,
  Copy,
  ArrowRight,
  Award,
  BookOpenText,
  Coins,
  GraduationCap,
  School,
  KeyRound,
  ShoppingBag,
  Settings2,
  UsersRound,
} from "lucide-react";
import TopNavigator from "../components/TopNavigator";
import Footer from "../components/Footer";
import LottieAnimation from "../components/LottieAnimation";
import Skyline from "../assets/london-skyline.jpg";
import BookmarkAnimation from "../assets/bookmark_front_.json";
import useCtx from "../hooks/useCts";

const HomeScreen: React.FC = () => {
  const ctx = useCtx();
  const [copiedToken, setCopiedToken] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isBooksBookmarkLoaded, setIsBooksBookmarkLoaded] = useState(false);
  const booksCardRef = useRef<HTMLAnchorElement | null>(null);
  const booksBookmarkLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const hasPlayedBooksBookmarkRef = useRef(false);

  const userClient = ctx.currentClient;

  const school = ctx.schools.find((item) => item.id === userClient?.schoolId);
  console.log(school);
  const teacherCount = userClient?.teachers?.length ?? 0;
  const studentCount = ctx.students.filter(
    (student) => student.schoolToken === userClient?.token,
  ).length;

  const copyTokenToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 5000);
      })
      .catch((err) => {
        console.error("Błąd kopiowania: ", err);
      });
  };

  const actions = [
    {
      to: "/zamowienia",
      title: "Zamówienia",
      description: "Składaj nowe zamówienia i kontroluj historię zakupów.",
      eyebrow: "Zakupy i statusy",
      Icon: ShoppingBag,
      cardClassName: "border-white/50 bg-white/10",
      iconClassName: "bg-white/50",
    },
    {
      to: "/zarzadzanie",
      title: "Strefa Admina",
      description:
        "Dodawaj lektorów, przeglądaj testy poziomujące ucznówi i zarządzaj ustawieniami.",
      eyebrow: "Zespół i konfiguracja",
      Icon: Settings2,
      cardClassName: "border-white/50 bg-white/10",
      iconClassName: "bg-white/50",
    },
    {
      to: "/podreczniki",
      title: "Podręczniki",
      description: "Korzystaj z cyfrowych podręczników i dodatkowych ćwiczeń.",
      eyebrow: "Biblioteka cyfrowa",
      Icon: BookOpenText,
      cardClassName: "border-white/50 bg-white/10",
      iconClassName: "bg-white/50",
    },
    {
      to: "/program-lojalnosciowy",
      title: "Program lojalnościowy",
      description:
        "Śledź punkty, korzyści i dostępne nagrody dla Twojej szkoły.",
      eyebrow: "Punkty i benefity",
      Icon: Coins,
      cardClassName: "border-white/50 bg-white/10",
      iconClassName: "bg-white/50",
    },
  ] as const;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);

    updateViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateViewport);

      return () => {
        mediaQuery.removeEventListener("change", updateViewport);
      };
    }

    mediaQuery.addListener(updateViewport);

    return () => {
      mediaQuery.removeListener(updateViewport);
    };
  }, []);

  useEffect(() => {
    if (!isBooksBookmarkLoaded) return;

    if (!isMobileViewport) {
      hasPlayedBooksBookmarkRef.current = true;
      booksBookmarkLottieRef.current?.setSpeed(1);
      booksBookmarkLottieRef.current?.goToAndPlay(0, true);
      return;
    }

    hasPlayedBooksBookmarkRef.current = false;
    booksBookmarkLottieRef.current?.stop();
    booksBookmarkLottieRef.current?.goToAndStop(0, true);
  }, [isBooksBookmarkLoaded, isMobileViewport]);

  useEffect(() => {
    if (
      !isMobileViewport ||
      !isBooksBookmarkLoaded ||
      !booksCardRef.current ||
      hasPlayedBooksBookmarkRef.current
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasPlayedBooksBookmarkRef.current) return;

        hasPlayedBooksBookmarkRef.current = true;
        booksBookmarkLottieRef.current?.setSpeed(1);
        booksBookmarkLottieRef.current?.goToAndPlay(0, true);
        observer.disconnect();
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(booksCardRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isBooksBookmarkLoaded, isMobileViewport]);

  return (
    <>
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#f7f2ea]">
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-40 "
          style={{ backgroundImage: `url(${Skyline})` }}
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
        />

        <div className="relative z-10 flex flex-1 flex-col">
          <TopNavigator />
          <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-16 pt-6 sm:px-6 lg:px-8">
            <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-start">
              <div className="px-1 pt-4 sm:pt-8 lg:pt-12">
                <h1 className="max-w-3xl bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text pb-1 text-3xl font-semibold leading-[1.12] tracking-[-0.04em] text-transparent sm:text-4xl lg:text-5xl">
                  {school?.name || "Nazwa szkoly"}, witaj w panelu klienta!
                </h1>
                <h2 className="mt-5 max-w-3xl font-medium leading-8 tracking-[-0.03em] text-slate-700 sm:leading-9 lg:text-[1.9rem]">
                  Korzystaj z cyfrowych podręczników, składaj wygodnie
                  zamówienia i zarządzaj ustawieniami konta Twojej szkoły.
                </h2>
                <Link
                  to="/zarzadzanie"
                  state={{ activeTab: "certificates" }}
                  className="mt-6 inline-flex items-center gap-3 rounded-lg border border-[#1b2430] bg-[#1b2430] px-5 py-3 text-left text-white shadow-[0_0_0_1px_rgba(255,188,8,0.28),0_0_28px_rgba(255,188,8,0.42),0_18px_44px_rgba(27,36,48,0.28)] transition hover:-translate-y-0.5 hover:bg-[#243244] hover:shadow-[0_0_0_1px_rgba(255,188,8,0.4),0_0_36px_rgba(255,188,8,0.56),0_22px_54px_rgba(27,36,48,0.32)]"
                >
                  <Award size={18} strokeWidth={2.2} className="text-white" />
                  <span className="text-base font-semibold tracking-[-0.02em] text-white">
                    Generator certyfikatów
                  </span>
                  <span className="rounded-md bg-[#ffbc08] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-black">
                    nowość
                  </span>
                </Link>
              </div>

              <aside className="hidden w-full sm:block lg:max-w-[360px] lg:justify-self-end">
                <div className="rounded-[28px] border border-white/50 bg-white/10 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.20)] backdrop-blur-md sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                        <School
                          size={18}
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Twoje konto
                      </p>
                      <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                        Profil szkoły
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <UsersRound
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Lektorzy</p>
                        </div>
                        <p className="mt-2 text-2xl font-semibold text-slate-900">
                          {teacherCount}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <GraduationCap
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Uczniowie</p>
                        </div>
                        <p className="mt-2 text-2xl font-semibold text-slate-900">
                          {studentCount}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <KeyRound
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Token szkoły</p>
                        </div>
                        {userClient?.token && (
                          <button
                            type="button"
                            onClick={() =>
                              copyTokenToClipboard(userClient.token)
                            }
                            className="rounded-full border border-slate-200 bg-white/85 p-2 transition hover:bg-slate-50"
                            title={copiedToken ? "Skopiowano!" : "Kopiuj token"}
                          >
                            {copiedToken ? (
                              <Check
                                size={16}
                                strokeWidth={2.4}
                                className="text-green-500"
                              />
                            ) : (
                              <Copy size={16} strokeWidth={2.2} />
                            )}
                          </button>
                        )}
                      </div>
                      <p className="mt-2 break-all text-base font-medium text-slate-800">
                        {userClient?.token || "Brak tokenu"}
                      </p>
                    </div>
                  </div>
                </div>
              </aside>
            </section>

            <section className="mt-10">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 ">
                {actions.map(
                  ({
                    to,
                    title,
                    description,
                    Icon,
                    cardClassName,
                    iconClassName,
                  }) => (
                    <Link
                      key={to}
                      to={to}
                      ref={to === "/podreczniki" ? booksCardRef : undefined}
                      aria-label={`${title}. ${description}`}
                      className={`group relative min-h-[250px] rounded-[28px] border p-5 text-left shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-white hover:shadow-[0_24px_70px_rgba(148,163,184,0.24)] hover:bg-white/40 ${cardClassName}`}
                    >
                      {to === "/podreczniki" && (
                        <div className="pointer-events-none absolute right-3 -top-10 z-10">
                          <LottieAnimation
                            animationData={BookmarkAnimation}
                            autoplay={!isMobileViewport}
                            loop={false}
                            className="h-32 w-32"
                            lottieRef={booksBookmarkLottieRef}
                            onDOMLoaded={() => {
                              setIsBooksBookmarkLoaded(true);

                              if (isMobileViewport) {
                                booksBookmarkLottieRef.current?.stop();
                                booksBookmarkLottieRef.current?.goToAndStop(
                                  0,
                                  true,
                                );
                              }
                            }}
                          />
                        </div>
                      )}

                      <div className="flex h-full flex-col">
                        <div className="flex items-center gap-4">
                          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                            <div
                              className={`flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5  ${iconClassName}`}
                            >
                              <Icon
                                className="h-5.5 w-5.5"
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <h3 className="min-w-0 text-lg font-semibold tracking-[-0.02em] text-slate-900 sm:text-xl">
                            {title}
                          </h3>
                        </div>

                        <div className="mt-7">
                          <p className="flex px-4 items-start gap-3 text-sm font-medium leading-7 text-slate-700">
                            <span>{description}</span>
                          </p>
                        </div>

                        <div className="mt-auto px-4 flex items-center justify-between pt-8 text-sm font-semibold text-slate-900">
                          <span>Otwórz sekcję</span>
                          <ArrowRight
                            size={20}
                            className="transition duration-300 group-hover:translate-x-1"
                            strokeWidth={2.4}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </Link>
                  ),
                )}
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default HomeScreen;
